import {
  createContext,
  Dispatch,
  FC,
  ReactElement,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { routePolicies } from "../constants/policies";
import { useUserContext } from "./UserContextProvider";

export const defaultValues = new Map();

interface IPoliciesContext {
  policies: Map<string, any>;
  setPolicies: Dispatch<SetStateAction<Map<string, any>>>;
  clearPolicies: () => void;
}

const PoliciesContext = createContext<IPoliciesContext>({
  policies: defaultValues,
  setPolicies: () => null,
  clearPolicies: () => null,
});

export const PoliciesContextProvider: FC<{ children: ReactElement }> = ({
  children,
}) => {
  const { pathname } = useLocation();
  const { user } = useUserContext();
  const [policies, setPolicies] = useState(defaultValues);

  const handleClearPolicies = () => {
    setPolicies(defaultValues);
  };

  // Check policy and route and redirect user if there is no policy to the nearest available route
  const findNearestAccessibleRoute = () => {
    const activePolicies = getActivePolicies(
      user.selectedCompanyMembership?.role?.policies as any
    );

    if (!(pathname in routePolicies)) {
      return null;
    }

    if (hasAccessToRoute(pathname, activePolicies)) {
      return null;
    }

    const availableRoutes = Object.entries(routePolicies)
      .filter(([_, policies]) =>
        policies.some((policy) => activePolicies?.includes(policy))
      )
      .map(([route]) => route);

    const sameCategoryRoutes = availableRoutes.filter((route) =>
      route.startsWith(pathname.split("/").slice(0, -1).join("/"))
    );

    const nearestRoute =
      sameCategoryRoutes.length > 0
        ? sameCategoryRoutes[0]
        : availableRoutes[0] || null;

    if (nearestRoute && nearestRoute !== pathname) {
      window.location.href = nearestRoute;
    }
  };

  useEffect(() => {
    findNearestAccessibleRoute();
  }, [pathname, user.selectedCompanyMembership?.role?.policies]);

  return (
    <PoliciesContext.Provider
      value={{
        policies,
        setPolicies,
        clearPolicies: handleClearPolicies,
      }}
    >
      {children}
    </PoliciesContext.Provider>
  );
};

export const usePoliciesContext = () => {
  return useContext(PoliciesContext);
};

const getActivePolicies = (
  localPolicies?: Record<string, boolean>
): string[] | undefined => {
  if (localPolicies) {
    return Object.keys(localPolicies).filter((policy) => localPolicies[policy]);
  }
  return undefined;
};

const hasAccessToRoute = (
  route: string,
  activePolicies?: string[]
): boolean => {
  const policies = routePolicies[route] || [];
  return policies.some((policy) => activePolicies?.includes(policy));
};
